import { useState } from 'react';
import { Link } from 'react-router-dom';
import { timeAgo } from '@dashboard/shared/timeAgo';
import type { Order } from '@shared/types';

interface Props {
  orders: Order[];
}

export default function PendingOrders({ orders }: Props) {
  const pending = orders.filter(
    (o) => o.status === 'pending' || o.status === 'payment_initiated' || o.status === 'usdc_received' || o.status === 'forwarding',
  );

  if (pending.length === 0) return null;

  const THIRTY_MIN = 30 * 60 * 1000;

  return (
    <div
      className="rounded p-5"
      style={{
        border: '1px solid var(--dash-border)',
        background: 'var(--dash-card)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="font-mono text-[10px] uppercase tracking-widest"
          style={{ color: 'var(--dash-text-dim)' }}
        >
          Pending Orders
        </div>
        <span
          className="font-mono text-xs"
          style={{ color: 'var(--dash-text-muted)' }}
        >
          {pending.length}
        </span>
      </div>
      <div className="space-y-2">
        {pending.slice(0, 8).map((o) => {
          const elapsed = Date.now() - new Date(o.created_at).getTime();
          const isLong = elapsed > THIRTY_MIN;
          return (
            <PendingOrderRow key={o.id} order={o} isLong={isLong} />
          );
        })}
      </div>
    </div>
  );
}

function PendingOrderRow({ order, isLong }: { order: Order; isLong: boolean }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`flex items-center justify-between px-3 py-2 rounded transition-colors ${
        isLong ? 'border border-amber-500/20 bg-amber-500/5' : ''
      }`}
      style={
        isLong
          ? undefined
          : {
              border: '1px solid var(--dash-border-light)',
              background: 'var(--dash-input)',
            }
      }
    >
      <Link
        to={`/dashboard/crypto/orders/${order.id}`}
        className="font-mono text-xs transition-colors"
        style={{ color: hovered ? 'var(--dash-text-primary)' : 'var(--dash-text-tertiary)' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {order.id.slice(0, 8)}...
      </Link>
      <span
        className="font-mono text-xs"
        style={{ color: 'var(--dash-text-muted)' }}
      >
        ${order.expected_amount.toFixed(2)}
      </span>
      <span className={`font-mono text-xs ${isLong ? 'text-amber-400' : ''}`}
        style={isLong ? undefined : { color: 'var(--dash-text-dim)' }}
      >
        {timeAgo(order.created_at)}
      </span>
    </div>
  );
}
